import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DollarSign } from "lucide-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Empty, Loading, Page, StatusPill, cx } from "../components/wf";
import { api, emailResultMessage, money, num } from "../lib/api";
import { useLabels, useStatuses } from "../lib/queries";

function SplitRows({ rows, testPrefix }) {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-3">
      {rows.map(([l, v, k]) => (
        <div key={k} className="flex justify-between gap-2 border-b border-wf-line py-1">
          <span className="text-wf-mute">{l}</span>
          <span data-testid={`${testPrefix}-${k}`} className={cx("font-bold", v < 0 && "text-wf-err")}>{typeof v === "number" && k !== "man_hours" ? money(v) : num(v)}</span>
        </div>
      ))}
    </div>
  );
}

function JobRow({ job, rows, myCut, statuses, onStatus }) {
  return (
    <div data-testid={`money-job-${job.id}`} className="rounded-2xl border border-wf-line p-5">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <Link to={`/work-orders/${job.id}`} className="min-w-0 hover:underline">
          <p className="truncate text-[17px] font-bold">{job.customer_name}</p>
          <p className="truncate text-sm text-wf-ink2">{job.job_description}</p>
        </Link>
        <div className="flex items-center gap-3">
          {onStatus ? (
            <select data-testid={`money-job-status-${job.id}`} value={job.status} onChange={(e) => onStatus(job, e.target.value)} className="h-9 rounded-lg border border-wf-line bg-wf-s2 px-2 text-sm font-semibold">
              {statuses.map((s) => <option key={s}>{s}</option>)}
            </select>
          ) : <StatusPill label={job.status} />}
          <div className="text-right">
            <p className="text-[11px] font-bold uppercase text-green-700">{myCut}</p>
            <p className={cx("text-xl font-extrabold", job.my_cut < 0 ? "text-wf-err" : "text-green-700")}>{money(job.my_cut)}</p>
          </div>
        </div>
      </div>
      <SplitRows rows={rows(job)} testPrefix={`job-${job.id}`} />
    </div>
  );
}

function MoneyHero({ label, value, sub }) {
  return (
    <div className="mb-6 rounded-2xl bg-green-700 p-6 text-white" data-testid="money-hero">
      <p className="text-sm font-semibold opacity-85">{label}</p>
      <p className="text-5xl font-extrabold tracking-tight" data-testid="money-hero-value">{money(value)}</p>
      {sub && <p className="mt-1 text-sm opacity-85">{sub}</p>}
    </div>
  );
}

const budgetRows = (take) => (s) => [
  ["Budget", s.budget, "budget"], [`${take}'s Take`, s.platinum_take, "platinum_take"], [`After ${take}`, s.after_platinum, "after_platinum"],
  ["Labor Cost", s.labor_cost, "labor_cost"], ["Per Diems", s.total_per_diems, "total_per_diems"], ["Expenses", s.total_expenses, "total_expenses"],
  ["Project Gross", s.project_gross, "project_gross"], ["Total Each", s.total_each, "total_each"],
];

export function BudgetJobs() {
  const labels = useLabels();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["ny-jobs"], queryFn: () => api("/ny-jobs") });
  const st = useStatuses();
  const [filter, setFilter] = useState("All");
  const statusNames = (st.data ?? []).map((s) => s.name);
  const changeStatus = async (job, status) => {
    try {
      const wo = await api(`/work-orders/${job.id}`);
      const body = { customer_id: wo.customer_id, job_description: wo.job_description, description_details: wo.description_details, hours_availability: wo.hours_availability,
        status, job_notes: wo.job_notes, job_photos: wo.job_photos, drive_link: wo.drive_link, budget: wo.budget, assigned_to: wo.assigned_to };
      const res = await api(`/work-orders/${job.id}`, { method: "PUT", body });
      qc.setQueryData(["work-order", job.id], res);
      qc.invalidateQueries({ queryKey: ["ny-jobs"] });
      qc.invalidateQueries({ queryKey: ["work-orders"] });
      if (res.email_result) (res.email_result.ok ? toast.success : toast.error)(emailResultMessage(res.email_result));
      else toast.success(`Status: ${status}`);
    } catch (e) {
      toast.error(e.message);
    }
  };
  if (q.isLoading) return <Loading />;
  const d = q.data;
  const jobs = (d?.jobs ?? []).filter((j) => filter === "All" || j.status === filter);
  const total = jobs.reduce((a, j) => a + (j.my_cut || 0), 0);
  const used = ["All", ...new Set((d?.jobs ?? []).map((j) => j.status))];
  return (
    <Page title={labels.ny_tab} subtitle={d ? `${d.take_name} takes ${d.pct}% off the top` : ""} wide testid="budget-jobs-page" titleClass="text-green-700">
      {!d ? <Empty title="Couldn't load" subtitle={q.error?.message} /> : (<>
        <MoneyHero label={`Total ${labels.my_cut} · ${jobs.length} ${filter === "All" ? "" : `${filter} `}job${jobs.length === 1 ? "" : "s"}`} value={filter === "All" ? d.totals?.my_cut ?? total : total} />
        <div className="mb-5 flex flex-wrap gap-1.5">
          {used.map((s) => (
            <button key={s} data-testid={`budget-filter-${s}`} onClick={() => setFilter(s)} className={cx("rounded-full px-3 py-1.5 text-xs font-bold", filter === s ? "bg-green-700 text-white" : "bg-wf-s3 text-wf-ink2")}>{s}</button>
          ))}
        </div>
        {!jobs.length ? <Empty icon={DollarSign} title="No budget jobs" subtitle={`Jobs with a budget and a ${labels.ny_location} Kastle PM show here.`} /> : (
          <div className="flex flex-col gap-3">
            {jobs.map((j) => <JobRow key={j.id} job={j} rows={budgetRows(d.take_name)} myCut={labels.my_cut} statuses={statusNames} onStatus={changeStatus} />)}
          </div>
        )}
      </>)}
    </Page>
  );
}

export function HourlyJobs() {
  const labels = useLabels();
  const q = useQuery({ queryKey: ["hourly-jobs"], queryFn: () => api("/hourly-jobs") });
  if (q.isLoading) return <Loading />;
  const d = q.data;
  const rows = (s) => [
    ["Man Hours", s.man_hours, "man_hours"], ["Hourly Rate", s.hourly_rate, "hourly_rate"], ["Amount", s.amount, "amount"],
    [`${d.take_name}'s Take`, s.platinum_take, "platinum_take"], [`After ${d.take_name}`, s.after_platinum, "after_platinum"], ["Labor Cost", s.labor_cost, "labor_cost"],
    ["Expenses", s.total_expenses, "total_expenses"], ["Project Gross", s.project_gross, "project_gross"], ["Total Each", s.total_each, "total_each"],
  ];
  return (
    <Page title="Hourly Jobs" subtitle="Man hours × hourly rate payout" wide testid="hourly-jobs-page" titleClass="text-green-700">
      {!d ? <Empty title="Couldn't load" subtitle={q.error?.message} /> : (<>
        <MoneyHero label={`Total ${labels.my_cut} · ${d.jobs.length} job${d.jobs.length === 1 ? "" : "s"}`} value={d.jobs.reduce((a, j) => a + (j.my_cut || 0), 0)} sub={`${d.take_name} takes ${d.pct}% off the top`} />
        {!d.jobs.length ? <Empty icon={DollarSign} title="No hourly jobs yet" subtitle="Set a job's billing to Hourly to see its payout here." /> : (
          <div className="flex flex-col gap-3">{d.jobs.map((j) => <JobRow key={j.id} job={j} rows={rows} myCut={labels.my_cut} />)}</div>
        )}
      </>)}
    </Page>
  );
}
